import {
  useGetNominatedPurchaserDetailsViewQuery,
  useGetNominatedPurchaserIndexItemsForOrgQuery,
  useLatestRef,
} from "@fero/hooks";
import { useTradeSalesStore } from "@fero/stores";
import * as LV from "@fero/utils/loading-values";
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Grid,
  TextField,
} from "@mui/material";
import type {
  NominatedPurchaserDetailsView,
  NominatedPurchaserIndexItemView,
} from "adl-gen/ferovinum/app/api";
import { BaseWizardCard } from "components/library/widgets/wizard-card/base-wizard-card";
import { Loader } from "components/widgets/loader/loader";
import { type FC, useEffect, useRef, useState } from "react";
import { isLoaded } from "utils/utility-types";

interface PurchaserSelectorCardProps {
  selectedPurchaser?: NominatedPurchaserIndexItemView;
  onChange: (v: NominatedPurchaserDetailsView | null) => void;
}
export const PurchaserSelectorCard = (props: PurchaserSelectorCardProps) => {
  return (
    <BaseWizardCard
      title="Select Purchaser"
      inputElement={
        <Grid container>
          <Grid item xs={12} lg={6}>
            <NominatedPurchaserSelector {...props} />
          </Grid>
        </Grid>
      }
    />
  );
};

const renderInput = (params: AutocompleteRenderInputParams) => (
  <TextField {...params} label="Select Purchaser" name="Purchaser" required />
);

const hasSameNominatedPurchaserId = (
  option: NominatedPurchaserIndexItemView,
  value: NominatedPurchaserIndexItemView,
): boolean => option.nominatedPurchaserId === value.nominatedPurchaserId;

const getPurchaserName = (np: NominatedPurchaserIndexItemView) => np.name;

const NominatedPurchaserSelector: FC<PurchaserSelectorCardProps> = ({
  selectedPurchaser,
  onChange,
}) => {
  const {
    options: { isEditMode },
  } = useTradeSalesStore();
  const onChangeRef = useLatestRef(onChange);
  const selectedPurchaserRef = useLatestRef(selectedPurchaser);
  const initializedRef = useRef(false);

  const [selection, setSelection] =
    useState<NominatedPurchaserIndexItemView | null>(selectedPurchaser ?? null);

  const eventualOptions = LV.fromUseQueryResult(
    useGetNominatedPurchaserIndexItemsForOrgQuery(),
  );
  const options = LV.match<NominatedPurchaserIndexItemView[]>({
    loading: () => [],
    error: () => [],
    loaded: (ok) => {
      const newIds = ok.map(
        (x) => x.nominatedPurchaserId as string | undefined,
      );
      queueMicrotask(() =>
        setSelection((s) =>
          !newIds.includes(s?.nominatedPurchaserId) ? ok[0] : s,
        ),
      );
      return ok.sort((a, b) =>
        a.name.normalize().localeCompare(b.name.normalize()),
      );
    },
  })(eventualOptions);

  const details = useGetNominatedPurchaserDetailsViewQuery(
    selection?.nominatedPurchaserId,
  );
  useEffect(() => {
    if (details.isLoading) return;
    const initialized = initializedRef.current;
    const currentNominatedPurchaserId =
      selectedPurchaserRef.current?.nominatedPurchaserId;
    if (details.isError) {
      const next = null;
      if (!initialized || currentNominatedPurchaserId == next) return;
      initializedRef.current = true;
      return queueMicrotask(() => onChangeRef.current(next));
    }
    const next = details.data;
    if (
      initialized &&
      currentNominatedPurchaserId === next.nominatedPurchaserId
    )
      return;
    initializedRef.current = true;
    return queueMicrotask(() => onChangeRef.current(next));
  }, [details, onChangeRef, selectedPurchaserRef, initializedRef]);

  return (
    <Loader loadingStates={[eventualOptions]}>
      {isLoaded(eventualOptions) && (
        <Autocomplete<NominatedPurchaserIndexItemView>
          fullWidth
          disabled={isEditMode}
          value={selectedPurchaser ?? null}
          onChange={(_e, value) => setSelection(value)}
          getOptionLabel={getPurchaserName}
          options={options}
          renderInput={renderInput}
          isOptionEqualToValue={hasSameNominatedPurchaserId}
        />
      )}
    </Loader>
  );
};
